import React from "react";
import styled from "styled-components";
import { BookOpen, ChevronsUpDown, LifeBuoy } from "lucide-react";
import { AuthUser, LanguageNameSpaces } from "@shared/types";
import UserAvatar from "common/Avatars/UserAvatar";
import UserInfo from "features/userInfo/UserInfo";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { showIntercomSpace } from "utils/misc.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

interface NewLookSiderFooterProps {
  user: AuthUser;
  collapsed: boolean;
}

const ICON_PROPS = { size: 16, strokeWidth: 1.75 };

const NewLookSiderFooter = ({ user, collapsed }: NewLookSiderFooterProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isWhitelabel } = useBranding();
  const name = user.full_name || user.email;

  return (
    <Wrapper $collapsed={collapsed}>
      {/* Collapsed, the menu's own Help item holds these links. */}
      {!isWhitelabel && !collapsed && (
        <HelpLinks $collapsed={collapsed}>
          <HelpLink
            onClick={() => showIntercomSpace("help")}
            title={t("sider.how_to_use")}
          >
            <BookOpen {...ICON_PROPS} />
            {!collapsed && t("sider.how_to_use")}
          </HelpLink>
          <HelpLink
            onClick={() => showIntercomSpace("home")}
            title={t("sider.support")}
          >
            <LifeBuoy {...ICON_PROPS} />
            {!collapsed && t("sider.support")}
          </HelpLink>
        </HelpLinks>
      )}
      <UserInfo
        user={user}
        placement={collapsed ? "rightBottom" : "topLeft"}
        trigger={
          <Account $collapsed={collapsed}>
            <UserAvatar user={user} avatarSize={32} fallbackInitialsSize={5} />
            {!collapsed && (
              <>
                <Who>
                  <Name>{name}</Name>
                  {user.full_name && <Email>{user.email}</Email>}
                </Who>
                <ChevronsUpDown size={14} strokeWidth={1.75} />
              </>
            )}
          </Account>
        }
      />
    </Wrapper>
  );
};

export default React.memo(NewLookSiderFooter);

const Wrapper = styled.div<{ $collapsed: boolean }>`
  flex: none;
  padding: ${(p) => (p.$collapsed ? "8px 0 12px" : "8px 12px 12px")};
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$collapsed ? "center" : "stretch")};
  gap: ${(p) => (p.$collapsed ? 8 : 4)}px;
  border-top: ${(p) => (p.$collapsed ? p.theme.borders.lightest : "none")};
`;
const HelpLinks = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$collapsed ? "column" : "row")};
  gap: 4px;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: ${(p) => p.theme.borders.lightest};
`;
const HelpLink = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  font: inherit;
  font-size: 12.5px;
  cursor: pointer;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
    background: ${(p) => p.theme.colors.cardBgColor};
  }
`;
const Account = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  gap: 10px;
  padding: ${(p) => (p.$collapsed ? "2px" : "6px 8px")};
  cursor: pointer;
  border-radius: 12px;
  color: ${(p) => p.theme.colors.secondaryText};
  &:hover {
    background: ${(p) => p.theme.colors.cardBgColor};
  }
`;
const Who = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;
const Name = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Email = styled.div`
  font-size: 11.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
